import { Col, Form, Input, InputNumber, Modal, Row, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import showNotification from "@/components/common/notification";
import "./index.scss";
import { LocationApi } from "@/apis/location.api";
import { LocationRequestModel, LocationResponseModel } from "@/models/location";

export interface LocationUpdateModalProps {
  visible: boolean;
  onClose:(isUpdated?: boolean) => void; 
  reloadLocationData?: () => void;
  currentId?: string;
  parentItem?: LocationResponseModel | null;
}

export function ModalUpdateLocation(props: LocationUpdateModalProps) {
  const { t } = useTranslation();
  const [locationForm] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [isEditLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if(props.visible){
      if (props.currentId) {
        fetchLocationData(props.currentId);
      }
  
      locationForm.resetFields();
      locationForm.setFieldValue("locationParentId", props.parentItem?.id);
    }
  }, [props.visible]); // Empty dependency array ensures this runs once when the component mounts.

  const handleOk = () => {
    locationForm.submit();
  };

  const handleCancel = () => {
    locationForm.resetFields();
    props.onClose();
  };

  const handleCancelUpdated = () => {
    locationForm.resetFields();
    props.onClose(true);
  };

  const fetchLocationData = async (locationId: string) => {
    setLoading(true);
    try {
      const response = await LocationApi.getLocation(locationId);
      const data = response.data;

      locationForm.setFieldsValue({
        locationName: data.locationName,
        streetNumber: data.streetNumber,
        zone: data.zone,
        buildingNumber: data.buildingNumber,
        floorNumber: data.floorNumber,
        apartmentNumber: data.apartmentNumber,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  async function handleFormSubmit(values: any) {
    if (props.currentId) {
      await handleLocationOperation(values, true);
    } else {
      await handleLocationOperation(values, false);
    }
  }

  async function handleLocationOperation(values: any, isUpdate: boolean) {
    setEditLoading(true);
    try {
      const model: LocationRequestModel = {
        ...(isUpdate && { id: props.currentId }),
        parentId: values.locationParentId,
        locationName: values.locationName,
        optionalDescription: values.optionalDescription,
        streetNumber: values.streetNumber,
        zone: values.zone,
        buildingNumber: values.buildingNumber,
        floorNumber: values.floorNumber,
        apartmentNumber: values.apartmentNumber,
      };

      const data = isUpdate
        ? await LocationApi.updateLocation(model)
        : await LocationApi.createLocation(model);

      if (data) {
        showNotification("success", t(isUpdate ? "Update Location successfully." : "Create Location successfully."));
        props.reloadLocationData && props.reloadLocationData();
        handleCancelUpdated();
      }
    } catch (error) {
    } finally {
      setEditLoading(false);
    }
  }

  function LocationForm() {
    return (
      <>
        <Form
          className="modal-container"
          layout="vertical"
          labelWrap
          form={locationForm}
          onFinish={handleFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Location Name"
                name="locationName"
                rules={[{ required: true, message: 'Please input the location name!' }]}
              >
                <Input className="input-base" placeholder="Enter Location Name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Street Number"
                name="streetNumber"
              >
                <InputNumber className="input-base" style={{ width: '100%' }} placeholder="Enter Street Number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Zone"
                name="zone"
              >
                <Input className="input-base" placeholder="Enter Zone" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Building Number"
                name="buildingNumber"
              >
                <Input className="input-base" placeholder="Enter Building Number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Floor Number"
                name="floorNumber"
              >
                <Input className="input-base" placeholder="Enter Floor Number" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Apartment Number"
                name="apartmentNumber"
              >
                <InputNumber className="input-base" style={{ width: '100%' }} placeholder="Enter Apartment Number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {!props.currentId && props.parentItem && (
              <Col span={12}>
                <Form.Item
                  label="Parent Location"
                  name="locationParentName"
                >
                  <Input defaultValue={props?.parentItem.name} className="input-base" style={{ width: '100%' }} disabled />
                </Form.Item>

                <Form.Item
                  name="locationParentId"
                  hidden
                />
              </Col>
            )}
          </Row>

        </Form>
      </>
    );
  }

  return (
    <>
      <Modal
        className="modal-setup"
        style={{ top: 170 }}
        width={800}
        open={props.visible}
        title={props.currentId ? "Edit Location" : "New Location"}
        onCancel={handleCancel}
        okButtonProps={{ loading: isEditLoading }}
        onOk={handleOk}
        maskClosable={false}
      >
        <Spin spinning={isLoading}>
          <LocationForm />
        </Spin>     
      </Modal>
    </>
  );
}

export default ModalUpdateLocation;
